import { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, setToken, ApiError } from '../lib/api';
import { useI18n, setLang, LANGS, type Lang } from '../lib/i18n';
import { useTheme } from '../lib/theme';
import { cn } from '@/lib/utils';
import { Logo } from '../components/Logo';

interface LoginResponse {
  accessToken: string;
  user: { id: string; email: string };
  tenant: { id: string; name: string; role: string };
}

// 비밀번호 본체는 절대 저장하지 않는다 — 브라우저 비밀번호 매니저(autoComplete) 가
// 처리. 여기는 사용자가 "다음에 이메일/비번 다시 채우자" 의도를 표시한 플래그와
// 마지막 입력 이메일만 보관한다.
const REMEMBER_KEY = 'swk_admin_remember';
const REMEMBER_EMAIL_KEY = 'swk_admin_remember_email';

export function LoginPage({ onLogin }: { onLogin: () => void }) {
  const { t, lang } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const flag = window.localStorage.getItem(REMEMBER_KEY) === '1';
      setRemember(flag);
      if (flag) {
        const saved = window.localStorage.getItem(REMEMBER_EMAIL_KEY);
        if (saved) setEmail(saved);
      }
    } catch { /* private mode 등 — 무시 */ }
  }, []);

  const submit = async () => {
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      const body = await api<LoginResponse>('/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      if (body.tenant.role !== 'owner' && body.tenant.role !== 'admin') {
        setError(t('login.errRoleNotAllowed', { role: body.tenant.role }));
        return;
      }
      try {
        if (remember) {
          window.localStorage.setItem(REMEMBER_KEY, '1');
          window.localStorage.setItem(REMEMBER_EMAIL_KEY, email);
        } else {
          window.localStorage.removeItem(REMEMBER_KEY);
          window.localStorage.removeItem(REMEMBER_EMAIL_KEY);
        }
      } catch { /* ignore */ }
      setToken(body.accessToken, body.tenant.id);
      onLogin();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-2">
          <Logo className="h-8 w-auto text-foreground" />
          <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{t('login.title')}</div>
        </div>

        <form
          className="space-y-3"
          onSubmit={(e) => { e.preventDefault(); void submit(); }}
        >
          <Input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('login.emailPh')}
            autoComplete="email"
          />
          <Input
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('login.passwordPh')}
            autoComplete="current-password"
          />
          <label className="flex items-center gap-2 text-[12px] text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-border accent-foreground"
            />
            {t('login.remember')}
          </label>
          <Button
            type="submit"
            disabled={loading || !email || !password}
            className="w-full"
          >
            {loading ? t('common.confirming') : t('login.submit')}
          </Button>
        </form>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <p className="text-[11px] leading-relaxed text-muted-foreground">
          {t('login.help')}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-1 pt-2">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code as Lang)}
              className={cn(
                'rounded-md px-2 py-1 text-[11px] transition-colors',
                lang === l.code
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
              )}
            >
              {l.label}
            </button>
          ))}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={t(theme === 'dark' ? 'theme.light' : 'theme.dark')}
            title={t(theme === 'dark' ? 'theme.light' : 'theme.dark')}
            className="ml-1 flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          >
            {theme === 'dark' ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
